import { gql, useMutation, useQuery } from '@apollo/client'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import EventIcon from '@mui/icons-material/Event'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { useMemo, useState } from 'react'
import { useLocale, useNotify, useTranslate } from 'react-admin'
import { nhost } from '~/helpers'

/**
 * "Mein Konto" → the calendar on the phone: CalDAV from the calendar service
 * (nautix/calendar.mjs). An app password of its own, shown once; whether
 * changes on the device come back; whether the colleagues' appointments show
 * as a second, read-only calendar.
 */
const ACCOUNT = gql`
  query CalendarAccount($id: uuid!) {
    account: calendar_accounts_by_pk(user_id: $id) {
      user_id
      writable
      show_team
      created_at
      last_used_at
      last_client
    }
  }
`

const NEW_PASSWORD = gql`
  mutation CalendarNewPassword {
    calendar_new_password {
      username
      password
      url
    }
  }
`

const SETTINGS = gql`
  mutation CalendarSettings($id: uuid!, $set: calendar_accounts_set_input!) {
    update_calendar_accounts_by_pk(pk_columns: { user_id: $id }, _set: $set) {
      user_id
      writable
      show_team
    }
  }
`

const REMOVE = gql`
  mutation CalendarRemove($id: uuid!) {
    delete_calendar_accounts_by_pk(user_id: $id) {
      user_id
    }
  }
`

interface Issued {
  username: string
  password: string
  url: string
}

const Copyable = ({ value, testId }: { value: string; testId?: string }) => {
  const translate = useTranslate()
  return (
    <Stack direction='row' spacing={0.5} sx={{ alignItems: 'center' }}>
      <Box component='code' data-testid={testId} sx={{ fontSize: 15, wordBreak: 'break-all' }}>
        {value}
      </Box>
      <Tooltip title={translate('account.calendar.copy')}>
        <IconButton
          size='small'
          aria-label={translate('account.calendar.copy')}
          onClick={() => navigator.clipboard?.writeText(value).catch(() => {})}
        >
          <ContentCopyIcon fontSize='small' />
        </IconButton>
      </Tooltip>
    </Stack>
  )
}

export const CalendarCard = () => {
  const translate = useTranslate()
  const notify = useNotify()
  const locale = useLocale()
  const id = nhost.getUserSession()?.user?.id
  const { data, refetch, loading } = useQuery(ACCOUNT, {
    variables: { id },
    skip: !id,
    fetchPolicy: 'cache-and-network',
  })
  const [issue, { loading: issuing }] = useMutation(NEW_PASSWORD)
  const [save] = useMutation(SETTINGS)
  const [remove] = useMutation(REMOVE)
  const [issued, setIssued] = useState<Issued | null>(null)
  const [confirm, setConfirm] = useState<'password' | 'remove' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const dateTime = useMemo(
    () => new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }),
    [locale]
  )
  const account = data?.account ?? null
  const server = issued ? new URL(issued.url).host : window.location.host
  const davUrl = issued?.url ?? `${window.location.origin}/dav/`

  const newPassword = async () => {
    setConfirm(null)
    setError(null)
    try {
      const { data: result } = await issue()
      setIssued(result.calendar_new_password)
      refetch()
    } catch (e: any) {
      setError(e.message)
    }
  }

  const change = async (set: Record<string, boolean>) => {
    await save({ variables: { id, set } })
    notify('account.calendar.saved', { type: 'info' })
    refetch()
  }

  return (
    <Card variant='outlined' data-testid='calendar-card'>
      <CardContent>
        <Stack direction='row' spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
          <EventIcon />
          <Typography variant='h6'>{translate('account.calendar.title')}</Typography>
        </Stack>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2, maxWidth: 820 }}>
          {translate('account.calendar.intro')}
        </Typography>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {issued && (
          <Alert severity='success' sx={{ mb: 2 }} data-testid='calendar-issued'>
            <Typography variant='body2' sx={{ mb: 1 }}>
              {translate('account.calendar.issued')}
            </Typography>
            <Table size='small' sx={{ '& td': { borderBottom: 0, py: 0.25, pl: 0 } }}>
              <TableBody>
                <TableRow>
                  <TableCell>{translate('account.calendar.server')}</TableCell>
                  <TableCell>
                    <Copyable value={server} testId='calendar-server' />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{translate('account.calendar.url')}</TableCell>
                  <TableCell>
                    <Copyable value={davUrl} testId='calendar-url' />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{translate('account.calendar.username')}</TableCell>
                  <TableCell>
                    <Copyable value={issued.username} testId='calendar-username' />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{translate('account.calendar.password')}</TableCell>
                  <TableCell>
                    <Copyable value={issued.password} testId='calendar-password' />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <Button size='small' sx={{ mt: 1 }} onClick={() => setIssued(null)}>
              {translate('account.calendar.done')}
            </Button>
          </Alert>
        )}

        {!loading && !account && !issued && (
          <Button
            variant='contained'
            onClick={newPassword}
            disabled={issuing}
            data-testid='calendar-setup'
          >
            {translate('account.calendar.setup')}
          </Button>
        )}

        {account && (
          <>
            <Typography variant='body2' sx={{ mb: 1 }} data-testid='calendar-status'>
              {account.last_used_at
                ? translate('account.calendar.used', {
                    when: dateTime.format(new Date(account.last_used_at)),
                    client: account.last_client ?? '',
                  })
                : translate('account.calendar.unused')}
            </Typography>
            <Stack sx={{ mb: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={account.writable}
                    onChange={(e) => change({ writable: e.target.checked })}
                  />
                }
                label={translate('account.calendar.writable')}
              />
              <Typography variant='caption' color='text.secondary' sx={{ ml: 6, mt: -0.5, mb: 1 }}>
                {translate(
                  account.writable
                    ? 'account.calendar.writable_on'
                    : 'account.calendar.writable_off'
                )}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={account.show_team}
                    onChange={(e) => change({ show_team: e.target.checked })}
                  />
                }
                label={translate('account.calendar.show_team')}
              />
              <Typography variant='caption' color='text.secondary' sx={{ ml: 6, mt: -0.5 }}>
                {translate('account.calendar.show_team_hint')}
              </Typography>
            </Stack>
            <Stack direction='row' spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Button onClick={() => setConfirm('password')} disabled={issuing}>
                {translate('account.calendar.new_password')}
              </Button>
              <Button color='warning' onClick={() => setConfirm('remove')}>
                {translate('account.calendar.remove')}
              </Button>
            </Stack>
          </>
        )}

        <Accordion variant='outlined' disableGutters sx={{ mt: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant='subtitle2'>{translate('account.calendar.howto')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {(['iphone', 'mac', 'android', 'thunderbird', 'outlook'] as const).map((device) => (
              <Box key={device} sx={{ mb: 1.5 }}>
                <Typography variant='subtitle2'>
                  {translate(`account.calendar.devices.${device}.name`)}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {translate(`account.calendar.devices.${device}.steps`, { server, url: davUrl })}
                </Typography>
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      </CardContent>

      <Dialog open={confirm === 'password'} onClose={() => setConfirm(null)}>
        <DialogTitle>{translate('account.calendar.new_password_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{translate('account.calendar.new_password_hint')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>{translate('ra.action.cancel')}</Button>
          <Button variant='contained' onClick={newPassword}>
            {translate('account.calendar.new_password')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirm === 'remove'} onClose={() => setConfirm(null)}>
        <DialogTitle>{translate('account.calendar.remove_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{translate('account.calendar.remove_hint')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>{translate('ra.action.cancel')}</Button>
          <Button
            variant='contained'
            color='warning'
            onClick={async () => {
              setConfirm(null)
              setIssued(null)
              await remove({ variables: { id } })
              notify('account.calendar.removed', { type: 'info' })
              refetch()
            }}
          >
            {translate('account.calendar.remove')}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}

export default CalendarCard
